// In-game interface: orbs, skill bar, minimap, party frames, chat, plus all of the panels.

import { CLASSES, SKILLS } from '../../shared/classes';
import { formatClock } from '../../shared/closing';
import { RARITY_COLORS, RARITY_NAMES, STAT_LABELS, canUseItem, POTION_PRICE, MAX_POTIONS } from '../../shared/items';
import { dressedLook, gearLook } from '../../shared/look';
import { ME_DOWNED, PartyEntry } from '../../shared/protocol';
import { DIFFICULTIES, HUB_ZONE, REALMS, parseZone, zoneId } from '../../shared/realms';
import { RESPEC_COST, STAT_INFO, xpToNext } from '../../shared/stats';
import { Item, MAX_PLAYERS, Mods, SLOT_IDS, SlotId, StatKey, T_FLOOR, T_LIQUID, T_VOID, T_WALL, T_BLOCK, rv } from '../../shared/types';
import { analytics } from '../analytics';
import { audio } from '../audio';
import { textWidth, wrapText } from '../gfx/font';
import { blitHero, heroFrame, itemIcon, skillIcon } from '../gfx/sprites';
import { input } from '../input';
import { C, ui } from '../ui';
import { VoiceClient, VoiceMode } from '../voice';
import { GameState } from './state';
import { WorldRenderer, objLabel } from './render';

export type PanelId = 'char' | 'inv' | 'skills' | 'merchant' | 'trainer' | 'stash' | 'gate' | 'arena' | 'help' | 'menu' | 'voice';

const BIND_LABELS = ['L', 'R', '1', '2', '3', '4'];
const BAR_H = 34;
/** '#' is the speaker. 'w' is its sound waves, for someone whose mic is live. */
const SPEAKER = ['...#....', '..##..w.', '####.w.w', '####.w.w', '####.w.w', '..##..w.', '...#....'];
const VOICE_MODES: [VoiceMode, string, string][] = [
  ['off', 'Off', 'No mic and no voices, in any room.'],
  ['listen', 'Listen', 'You hear others. Your mic stays off.'],
  ['talk', 'Talk', 'You hear others. Hold V to talk.'],
];
const SLOT_LABELS: Record<SlotId, string> = {
  weapon: 'Weapon', offhand: 'Offhand', helm: 'Helm', armor: 'Armor', gloves: 'Gloves', boots: 'Boots', ring1: 'Ring', ring2: 'Ring', amulet: 'Amulet',
};

function fmt(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}m`;
  if (n >= 1e4) return `${Math.round(n / 1e3)}k`;
  return String(Math.round(n));
}

/** As much of a text as fits in a width, ending in a dot if any of it had to go. */
function clipText(text: string, width: number): string {
  if (textWidth(text) <= width) return text;
  let cut = text;
  while (cut && textWidth(`${cut}.`) > width) cut = cut.slice(0, -1);
  return `${cut}.`;
}

export class Hud {
  left: PanelId | null = null;
  right: PanelId | null = null;
  center: PanelId | null = null;
  automap = false;
  chatOpen = false;
  chatText = '';
  /** Called when the player wants to leave the room. */
  onLeave: (() => void) | null = null;
  private miniCanvas: HTMLCanvasElement | null = null;
  private clickedSpeaker: string | null = null;
  private miniGen = -1;

  constructor(public st: GameState, public world: WorldRenderer, public voice: VoiceClient) {}

  get anyPanel(): boolean {
    return !!(this.left || this.right || this.center);
  }

  togglePanel(p: PanelId): void {
    audio.play('click');
    if (this.left !== p && this.right !== p && this.center !== p) analytics.track('panel_opened', { panel: p });
    if (p === 'inv') {
      this.right = this.right === 'inv' ? null : 'inv';
    } else if (p === 'char' || p === 'skills') {
      this.left = this.left === p ? null : p;
    } else {
      this.center = this.center === p ? null : p;
    }
  }

  closeAll(): void {
    this.left = this.right = this.center = null;
  }

  /** Opens the interface for an NPC or object. */
  openFor(type: string, sub: string): void {
    if (type === 'npc' && sub === 'merchant') {
      this.left = 'merchant';
      this.right = 'inv';
    } else if (type === 'npc' && sub === 'trainer') {
      this.left = 'trainer';
    } else if (type === 'stash') {
      this.left = 'stash';
      this.right = 'inv';
    } else if (type === 'gate') {
      this.center = 'gate';
    } else if (type === 'portal' && sub === 'arena') {
      this.center = 'arena';
    }
  }

  draw(W: number, H: number): void {
    const st = this.st;
    if (!st.hero || !st.derived || !st.map) return;

    this.drawMinimap(W);
    this.drawVoiceStatus(this.drawParty());
    this.drawTargetBar(W);
    this.drawBossBar(W);
    const top = this.drawClosing(W);
    this.drawBanners(W, top);
    this.drawHint(W, top);
    this.drawChat(H);
    if (this.automap) this.drawAutomap(W, H);

    if (this.left === 'char') this.panelChar();
    if (this.left === 'skills') this.panelSkills();
    if (this.left === 'merchant') this.panelMerchant();
    if (this.left === 'trainer') this.panelTrainer();
    if (this.left === 'stash') this.panelStash();
    if (this.right === 'inv') this.panelInventory(W);
    if (this.center === 'gate') this.panelGate(W, H);
    if (this.center === 'arena') this.panelArena(W, H);
    if (this.center === 'help') this.panelHelp(W, H);
    if (this.center === 'menu') this.panelMenu(W, H);
    if (this.center === 'voice') this.panelVoice(W, H);

    // Party frames are drawn first, so only now can we tell if a panel was on top of the speaker.
    // A click meant for that panel mustn't change who you can hear.
    if (this.clickedSpeaker && !ui.onPanel) {
      audio.play('click');
      this.voice.toggleMute(this.clickedSpeaker);
    }
    this.clickedSpeaker = null;

    this.drawBar(W, H);
    this.drawDeath(W, H);
    this.drawConnection(W, H);
    ui.flushTooltip();
    this.drawWorldTooltip();
  }

  // ─── Bottom bar ─────────────────────────────────────────────────────────

  private drawOrb(cx: number, cy: number, r: number, frac: number, cols: [string, string, string], label: string): void {
    const g = ui.ctx;
    g.fillStyle = '#000000';
    for (let dy = -r - 2; dy <= r + 2; dy++) {
      const s = Math.floor(Math.sqrt(Math.max(0, (r + 2) * (r + 2) - dy * dy)));
      g.fillRect(cx - s, cy + dy, s * 2 + 1, 1);
    }
    g.fillStyle = C.gold;
    for (let i = 0; i < 80; i++) {
      const a = (i / 80) * 6.283;
      g.fillRect(Math.round(cx + Math.cos(a) * (r + 1)), Math.round(cy + Math.sin(a) * (r + 1)), 1, 1);
    }
    const t = performance.now() / 300;
    const top = Math.round(r - frac * 2 * r);
    for (let dy = -r; dy <= r; dy++) {
      const span = Math.floor(Math.sqrt(Math.max(0, r * r - dy * dy)));
      if (dy < top) {
        g.fillStyle = '#100808';
        g.fillRect(cx - span, cy + dy, span * 2 + 1, 1);
        continue;
      }
      // Dithered glass shading across the sphere.
      for (let dx = -span; dx <= span; dx++) {
        const shade = (dx + r * 0.3) * (dx + r * 0.3) + (dy + r * 0.3) * (dy + r * 0.3);
        const f = shade / (r * r * 1.4);
        const wave = dy === top ? Math.sin(dx * 0.7 + t) > 0 : false;
        const dit = (cx + dx + cy + dy) & 1;
        g.fillStyle = wave ? cols[2] : f < 0.3 ? cols[2] : f < 0.45 && dit ? cols[2] : f < 0.85 ? cols[1] : f < 1.1 && dit ? cols[1] : cols[0];
        g.fillRect(cx + dx, cy + dy, 1, 1);
      }
    }
    g.fillStyle = '#fcfcfc';
    g.fillRect(cx - Math.round(r * 0.45), cy - Math.round(r * 0.6), 3, 2);
    ui.text(label, cx, cy - 3, C.white, { align: 'center' });
  }

  private drawBar(W: number, H: number): void {
    const st = this.st;
    const hero = st.hero!;
    const y0 = H - BAR_H;
    const orbR = 25;

    // Stone strip.
    const barX = 40;
    const barW = W - 80;
    ui.rect(barX, y0, barW, BAR_H, '#000000');
    ui.rect(barX, y0 + 1, barW, BAR_H - 1, C.stone);
    const g = ui.ctx;
    g.fillStyle = '#2c2428';
    for (let py = y0 + 1; py < H; py += 2) {
      for (let px = barX + ((py >> 1) & 1); px < barX + barW; px += 3) g.fillRect(px, py, 1, 1);
    }
    ui.rect(barX, y0 + 1, barW, 1, C.gold);
    if (ui.hit(barX, y0, barW, BAR_H)) ui.captured = true;

    // XP track runs along the bottom edge.
    const xpW = barW - 8;
    const nextXp = xpToNext(hero.level);
    const xpFrac = hero.level >= 50 ? 1 : Math.min(1, st.me[5] / nextXp);
    ui.rect(barX + 4, H - 4, xpW, 3, '#000000');
    ui.rect(barX + 4, H - 4, Math.round(xpW * xpFrac), 3, C.goldL);
    for (let i = 1; i < 10; i++) ui.rect(barX + 4 + Math.round((xpW * i) / 10), H - 4, 1, 3, '#000000');
    if (ui.hit(barX + 4, H - 6, xpW, 6)) {
      ui.tooltip(() => ui.textBox([{ s: `Level ${hero.level}`, c: C.goldL }, { s: `XP: ${Math.floor(st.me[5])} / ${nextXp}` }]));
    }

    // Skill slots.
    const size = 22;
    const gap = 2;
    const totalSlots = 6;
    const potW = 2 * (size - 4) + 2;
    const sysW = 3 * 13 + 2;
    const rowW = totalSlots * (size + gap) + potW + sysW + 14;
    let x = Math.round(W / 2 - rowW / 2);
    const sy = y0 + 4;
    for (let i = 0; i < totalSlots; i++) {
      this.skillSlot(x, sy, size, i);
      x += size + gap;
      if (i === 1) x += 3;
    }
    x += 3;
    this.potionSlot(x, sy + 2, size - 4, 'hp', 'Q', st.me[7]);
    x += size - 2;
    this.potionSlot(x, sy + 2, size - 4, 'mp', 'E', st.me[8]);
    x += size;

    const pulse = Math.floor(performance.now() / 350) % 2 === 0;
    const statDue = hero.statPts > 0;
    const skillDue = hero.skillPts > 0;
    if (ui.button(x, sy, 13, 11, 'C', { col: statDue && pulse ? C.goldL : undefined, active: this.left === 'char' })) this.togglePanel('char');
    if (ui.button(x + 13, sy, 13, 11, 'I', { active: this.right === 'inv' })) this.togglePanel('inv');
    if (ui.button(x + 26, sy, 13, 11, 'K', { col: skillDue && pulse ? C.goldL : undefined, active: this.left === 'skills' })) this.togglePanel('skills');
    if (ui.button(x, sy + 11, 13, 11, 'T', {})) this.recall();
    if (ui.button(x + 13, sy + 11, 13, 11, '?', { active: this.center === 'help' })) this.togglePanel('help');
    if (ui.button(x + 26, sy + 11, 13, 11, '=', { active: this.center === 'menu' })) this.togglePanel('menu');

    this.drawOrb(orbR + 4, H - orbR - 3, orbR, st.me[2] ? st.me[0] / st.me[2] : 0, ['#600000', '#c80000', '#f85858'], `${fmt(st.me[0])}`);
    this.drawOrb(W - orbR - 4, H - orbR - 3, orbR, st.me[3] ? st.me[1] / st.me[3] : 0, ['#000060', '#0030c8', '#5888f8'], `${fmt(st.me[1])}`);
    if (ui.hit(4, H - orbR * 2 - 6, orbR * 2 + 4, orbR * 2 + 6)) {
      ui.captured = true;
      ui.tooltip(() => ui.textBox([{ s: `Life: ${Math.round(st.me[0])} / ${st.me[2]}`, c: '#f85858' }]));
    }
    if (ui.hit(W - orbR * 2 - 8, H - orbR * 2 - 6, orbR * 2 + 8, orbR * 2 + 6)) {
      ui.captured = true;
      ui.tooltip(() => ui.textBox([{ s: `Mana: ${Math.round(st.me[1])} / ${st.me[3]}`, c: '#5888f8' }]));
    }
    // Reminder of unspent points hovering above the bar.
    if (statDue || skillDue) {
      const s = [statDue ? `+${hero.statPts} stat` : '', skillDue ? `+${hero.skillPts} skill` : ''].filter(Boolean).join('  ');
      ui.text(s, W - 60, y0 - 9, pulse ? C.goldL : C.gold, { align: 'right' });
    }
  }

  recall(): void {
    const st = this.st;
    if (st.zone?.id !== HUB_ZONE) st.send({ t: 'warp', z: HUB_ZONE });
  }

  private skillSlot(x: number, y: number, size: number, i: number): void {
    const st = this.st;
    const k = st.hero!.binds[i];
    ui.rect(x, y, size, size, '#000000');
    ui.rect(x + 1, y + 1, size - 2, size - 2, '#181014');
    const hot = ui.hit(x, y, size, size);
    if (hot) ui.captured = true;
    if (k && SKILLS[k]) {
      const def = SKILLS[k];
      const rank = st.rankOf(k);
      ui.ctx.drawImage(skillIcon(def.icon), x + 3, y + 3);
      const cd = st.cooldowns.get(k);
      const now = performance.now();
      if (cd && cd.until > now) {
        const f = (cd.until - now) / (cd.dur * 1000);
        ui.rect(x + 1, y + 1, size - 2, Math.round((size - 2) * f), 'rgba(0,0,0,0.75)');
        const left = (cd.until - now) / 1000;
        ui.text(left >= 10 ? String(Math.ceil(left)) : left.toFixed(1), x + (size >> 1), y + 8, C.white, { align: 'center' });
      } else if (st.me[1] < rv(def.mana, rank)) {
        ui.rect(x + 1, y + 1, size - 2, size - 2, 'rgba(0,0,120,0.55)');
      }
      if (hot) ui.tooltip(() => this.skillTip(k));
    }
    ui.text(BIND_LABELS[i], x + 1, y + size - 8, i < 2 ? C.goldL : C.text);
    if (hot && input.lmbPressed) {
      if (this.left === 'skills') return;
      this.left = 'skills';
      audio.play('click');
    }
  }

  private potionSlot(x: number, y: number, size: number, k: 'hp' | 'mp', key: string, count: number): void {
    ui.rect(x, y, size, size, '#000000');
    ui.rect(x + 1, y + 1, size - 2, size - 2, '#181014');
    const hot = ui.hit(x, y, size, size);
    if (hot) ui.captured = true;
    ui.ctx.drawImage(itemIcon(k === 'hp' ? 'potHp' : 'potMp', 0), x + 3, y + 2);
    ui.text(String(count), x + size - 1, y + size - 8, count ? C.white : C.dim, { align: 'right' });
    ui.text(key, x + 1, y + 1, C.text);
    if (hot) {
      if (input.lmbPressed) this.st.send({ t: 'pot', k });
      ui.tooltip(() => ui.textBox([{ s: k === 'hp' ? 'Life Potion' : 'Mana Potion', c: k === 'hp' ? '#f85858' : '#5888f8' }, { s: k === 'hp' ? 'Restores 45% life over 2.5s' : 'Restores 55% mana over 2.5s' }]));
    }
  }

  skillTip(k: string): void {
    const st = this.st;
    const def = SKILLS[k];
    const base = st.hero!.skills[k] ?? 0;
    const rank = st.rankOf(k);
    const showRank = Math.max(rank, 1);
    const lines: { s: string; c?: string }[] = [{ s: def.name, c: C.goldL }];
    lines.push({ s: base ? `Rank ${base}/${def.maxRank}${rank > base ? ` (+${rank - base})` : ''}` : `Unlearned - needs level ${def.tier}`, c: base ? C.text : C.dim });
    for (const l of wrapText(def.desc, 190)) lines.push({ s: l, c: C.dim });
    for (const l of def.info(showRank)) lines.push({ s: l, c: C.cyan });
    const mana = rv(def.mana, showRank);
    const cd = rv(def.cd, showRank);
    const cost = [mana ? `Mana ${Math.round(mana)}` : '', cd ? `Cooldown ${cd.toFixed(1)}s` : ''].filter(Boolean).join('  ');
    if (cost) lines.push({ s: cost, c: '#5888f8' });
    if (base < def.maxRank && base) {
      lines.push({ s: 'Next rank:', c: C.dim });
      for (const l of def.info(rank + 1)) lines.push({ s: l, c: '#3c7c9c' });
    }
    ui.textBox(lines);
  }

  // ─── Minimap ────────────────────────────────────────────────────────────

  private ensureMini(): HTMLCanvasElement {
    const st = this.st;
    const map = st.map!;
    if (this.miniGen !== st.zoneGen || !this.miniCanvas) {
      const c = document.createElement('canvas');
      c.width = map.w;
      c.height = map.h;
      this.miniCanvas = c;
      this.miniGen = st.zoneGen;
    }
    const g = this.miniCanvas.getContext('2d')!;
    if (st.exploredNew.length) {
      const realm = st.realm!;
      for (const i of st.exploredNew) {
        const t = map.tiles[i];
        const x = i % map.w;
        const y = (i / map.w) | 0;
        g.fillStyle = t === T_FLOOR ? realm.floorPal[1] : t === T_WALL || t === T_BLOCK ? realm.wallPal[2] : t === T_LIQUID ? realm.liquidPal[1] : t === T_VOID ? '#100818' : '#000000';
        g.fillRect(x, y, 1, 1);
      }
      st.exploredNew.length = 0;
    }
    return this.miniCanvas;
  }

  private drawMinimap(W: number): void {
    const st = this.st;
    const mini = this.ensureMini();
    const size = 62;
    const x0 = W - size - 3;
    const y0 = 3;
    ui.rect(x0 - 2, y0 - 2, size + 4, size + 4, C.goldD);
    ui.rect(x0 - 1, y0 - 1, size + 2, size + 2, '#000000');
    const g = ui.ctx;
    g.save();
    g.beginPath();
    g.rect(x0, y0, size, size);
    g.clip();
    const scale = 1;
    const ox = x0 + (size >> 1) - Math.round(st.px) * scale;
    const oy = y0 + (size >> 1) - Math.round(st.py) * scale;
    g.drawImage(mini, ox, oy);
    this.drawMapMarkers(g, ox, oy, scale);
    g.restore();
    const name = st.zone!.name;
    const label = name.length > 34 ? `${name.slice(0, 33)}.` : name;
    ui.text(label, W - 3, y0 + size + 4, C.goldL, { align: 'right' });
    ui.text(`Room ${st.roomCode}${st.difficulty ? ` ${DIFFICULTIES[st.difficulty].name}` : ''}`, W - 3, y0 + size + 13, C.dim, { align: 'right' });
    if (st.ping > 0) ui.text(`${st.ping}ms`, W - 3, y0 + size + 22, st.ping > 200 ? C.orange : '#585858', { align: 'right' });
  }

  private drawMapMarkers(g: CanvasRenderingContext2D, ox: number, oy: number, scale: number): void {
    const st = this.st;
    for (const o of st.objects.values()) {
      if (!st.explored[Math.floor(o.y) * st.map!.w + Math.floor(o.x)]) continue;
      const col = o.type === 'stairsDown' ? '#f8d878' : o.type === 'portal' || o.type === 'gate' ? '#3cbcfc' : o.type === 'stairsUp' ? '#bcbcbc' : o.type === 'npc' || o.type === 'stash' ? '#58f898' : o.state ? '' : '#f8b800';
      if (!col) continue;
      g.fillStyle = col;
      g.fillRect(Math.round(ox + o.x * scale) - 1, Math.round(oy + o.y * scale) - 1, 3, 3);
    }
    for (const u of st.units.values()) {
      if (u.hp <= 0 || u.id === st.myUid) continue;
      if (u.kind !== 'player' && !st.isVisibleAt(u.x, u.y)) continue;
      g.fillStyle = u.kind === 'player' ? '#58f898' : u.tier === 3 ? '#fc74b4' : u.kind === 'summon' ? '#58f898' : '#f83800';
      g.fillRect(Math.round(ox + u.x * scale), Math.round(oy + u.y * scale), scale, scale);
    }
    g.fillStyle = '#fcfcfc';
    g.fillRect(Math.round(ox + st.px * scale) - 1, Math.round(oy + st.py * scale) - 1, 2, 2);
  }

  private drawAutomap(W: number, H: number): void {
    const st = this.st;
    const map = st.map!;
    const mini = this.ensureMini();
    const scale = Math.max(1, Math.min(Math.floor((W - 40) / map.w), Math.floor((H - 70) / map.h)));
    const ox = Math.round((W - map.w * scale) / 2);
    const oy = Math.round((H - BAR_H - map.h * scale) / 2);
    const g = ui.ctx;
    g.globalAlpha = 0.7;
    g.drawImage(mini, ox, oy, map.w * scale, map.h * scale);
    g.globalAlpha = 1;
    this.drawMapMarkers(g, ox, oy, scale);
  }

  // ─── Party & target ─────────────────────────────────────────────────────

  /** Returns the y position below what it drew. */
  private drawParty(): number {
    const st = this.st;
    const others = st.party.filter((p) => p.pid !== st.myPid);
    let y = 4;
    if (!others.length) return y;
    const isArena = st.zone?.pvp;
    for (const p of others) {
      this.partyFrame(4, y, p, !!isArena);
      y += 19;
    }
    if (isArena) {
      const me = st.party.find((p) => p.pid === st.myPid);
      if (me) ui.text(`Your kills: ${me.score}`, 4, y + 2, C.goldL);
      y += 11;
    }
    return y;
  }

  /** What voice is doing, for anyone who has it on. */
  private drawVoiceStatus(y: number): void {
    const v = this.voice;
    if (v.mode === 'off' || !v.available) return;
    let text: string;
    let col: string = C.dim;
    if (v.status === 'failed') {
      text = 'Voice lost. Press P';
      col = C.orange;
    } else if (v.status === 'reconnecting') {
      text = 'Voice reconnecting...';
      col = C.orange;
    } else if (v.status !== 'connected') {
      text = 'Voice connecting...';
    } else if (v.awaitingMic) {
      text = 'Allow your mic';
      col = C.orange;
    } else if (v.talking && v.micReady) {
      text = 'Talking';
      col = C.green;
    } else {
      text = v.mode === 'talk' ? 'Hold V to talk' : 'Listening';
    }
    this.speaker(4, y + 1, col, v.mode === 'talk', false);
    ui.text(text, 15, y + 1, col);
  }

  private speaker(x: number, y: number, col: string, waves: boolean, slashed: boolean): void {
    SPEAKER.forEach((row, py) => {
      for (let px = 0; px < row.length; px++) {
        if (row[px] === '#' || (row[px] === 'w' && waves)) ui.rect(x + px, y + py, 1, 1, col);
      }
    });
    if (slashed) for (let i = 0; i < 7; i++) ui.rect(x + 7 - i, y + i, 1, 1, C.red);
  }

  private partyFrame(x: number, y: number, p: PartyEntry, arena: boolean): void {
    // Party members in view carry their gear; others fall back to the bare class look.
    const unit = [...this.st.units.values()].find((u) => u.kind === 'player' && u.name === p.name);
    const look = unit ? this.st.look(unit) : CLASSES[p.cls as keyof typeof CLASSES]?.look;
    ui.rect(x, y, 86, 17, '#000000');
    ui.rect(x + 1, y + 1, 84, 15, '#181014');
    if (look) {
      // Head and shoulders: the hero sprite's rows that the old 16x18 sprite showed at 0..10.
      const spr = heroFrame(look, 0, 0);
      ui.ctx.drawImage(spr, 0, 8, 32, 22, x + 1, y + 2, 16, 11);
    }
    const voice = this.voice.entry(p.pid);
    // The hover tooltip has the full name.
    ui.text(clipText(p.name, voice ? 56 : 66), x + 18, y + 1, p.dead ? C.orange : C.text, { shadow: null });
    ui.rect(x + 18, y + 9, 64, 2, '#400000');
    ui.rect(x + 18, y + 9, Math.round((64 * p.hp) / 100), 2, '#d82800');
    ui.rect(x + 18, y + 12, 64, 2, '#000040');
    ui.rect(x + 18, y + 12, Math.round((64 * p.mp) / 100), 2, '#2860f8');
    if (voice) this.partySpeaker(x + 75, y + 1, p.pid, voice.vid, voice.gen !== undefined);
    if (ui.hit(x, y, 86, 17)) {
      ui.captured = true;
      const zone = parseZone(p.zone).name;
      // Runs after the panels are drawn. A frame hidden by one has nothing to say.
      ui.tooltip(() => {
        if (ui.onPanel) return;
        ui.textBox([
          { s: `${p.name}, level ${p.level} ${CLASSES[p.cls as keyof typeof CLASSES]?.name ?? ''}`, c: C.goldL },
          { s: zone, c: C.dim },
          ...(arena ? [{ s: `Kills: ${p.score}` }] : []),
          ...(voice ? [{ s: this.voice.isMuted(voice.vid) ? 'Muted. Click the speaker to unmute' : 'Click the speaker to mute', c: C.dim }] : []),
        ]);
      });
    }
  }

  private partySpeaker(x: number, y: number, pid: number, vid: string, hasMic: boolean): void {
    const muted = this.voice.isMuted(vid);
    const hot = ui.hit(x - 1, y - 1, 10, 9);
    ui.rect(x - 1, y - 1, 10, 9, hot ? C.stone : '#181014');
    const col = muted ? C.dim : this.voice.isSpeaking(pid) ? C.green : hot ? C.goldL : C.dim;
    this.speaker(x, y, col, hasMic, muted);
    if (hot && input.lmbPressed) this.clickedSpeaker = vid;
  }

  private drawTargetBar(W: number): void {
    const u = this.world.hoverUnit;
    if (!u || u.tier === 3 || u.id === this.st.myUid) return;
    const w = 130;
    const x = (W - w) >> 1;
    const y = 3;
    const dead = u.hp <= 0;
    const myTeam = this.st.units.get(this.st.myUid)?.team ?? 1;
    const hostile = u.team !== myTeam;
    const nameCol = u.kind === 'player' ? (hostile ? C.orange : '#58f898') : u.tier === 2 ? C.goldL : u.tier === 1 ? '#6888fc' : hostile ? C.white : '#58f898';
    ui.rect(x, y, w, 11, '#000000');
    ui.rect(x + 1, y + 1, w - 2, 9, '#400000');
    if (!dead) ui.rect(x + 1, y + 1, Math.round(((w - 2) * u.hp) / 100), 9, '#a81000');
    const label = u.kind === 'player' && dead ? `${u.name} - revive` : `${u.name}${u.level ? ` (${u.level})` : ''}`;
    ui.text(label, W >> 1, y + 2, nameCol, { align: 'center' });
    if (u.extra && u.kind === 'monster') ui.text(u.extra, W >> 1, y + 13, '#6888fc', { align: 'center' });
  }

  private drawBossBar(W: number): void {
    const st = this.st;
    let boss = null;
    for (const u of st.units.values()) {
      if (u.tier === 3 && u.hp > 0 && u.kind === 'monster') {
        boss = u;
        break;
      }
    }
    if (!boss) return;
    const w = Math.min(260, W - 200);
    const x = (W - w) >> 1;
    const y = 4;
    ui.rect(x - 2, y - 2, w + 4, 14, C.gold);
    ui.rect(x - 1, y - 1, w + 2, 12, '#000000');
    ui.rect(x, y, w, 10, '#300000');
    ui.rect(x, y, Math.round((w * boss.hp) / 100), 10, '#c80000');
    ui.rect(x, y, Math.round((w * boss.hp) / 100), 2, '#f85858');
    ui.text(boss.name, W >> 1, y + 2, C.white, { align: 'center' });
  }

  /**
   * The countdown when a room is closing for an update: quiet at first, big for the last minute.
   * Sits under the target and boss bars. Returns where the things below it should start.
   */
  private drawClosing(W: number): number {
    const left = this.st.closingLeft();
    if (left === null) return 26;
    const big = left <= 60;
    const scale = big ? 2 : 1;
    const text = `Room closes in ${formatClock(left)}`;
    const tw = textWidth(text, scale);
    const bh = 8 * scale + 3;
    const y = 17;
    const g = ui.ctx;
    g.globalAlpha = 0.55;
    ui.rect(((W - tw) >> 1) - 4, y - 2, tw + 8, bh, '#000000');
    g.globalAlpha = 1;
    const flash = left <= 10 && Math.floor(performance.now() / 250) % 2 === 0;
    ui.text(text, W >> 1, y, flash ? C.white : big || left <= 300 ? C.orange : C.goldL, { align: 'center', scale });
    return y - 2 + bh + 1;
  }

  /** A nudge for brand new heroes; it goes away with the first kill. */
  private drawHint(W: number, top: number): void {
    const st = this.st;
    const hero = st.hero!;
    if (hero.kills > 0 || hero.level > 1 || this.anyPanel || st.banners.length) return;
    const pulse = Math.floor(performance.now() / 500) % 2 === 0;
    const text = st.zone?.safe ? 'Click the Waygate to begin your journey' : 'Click monsters to attack. Right click uses your second skill.';
    ui.text(text, W >> 1, Math.max(22, top), pulse ? C.goldL : C.gold, { align: 'center' });
  }

  private drawBanners(W: number, top: number): void {
    const st = this.st;
    // Sits above the hero's head; never over them. Extra banners shrink to fit the space.
    let y = top;
    const big = st.banners.length <= 2;
    for (const b of st.banners.slice(-4)) {
      const a = b.t < 0.2 ? b.t / 0.2 : b.t > 2.8 ? Math.max(0, (3.5 - b.t) / 0.7) : 1;
      const g = ui.ctx;
      const scale = big && textWidth(b.text, 2) < W - 20 ? 2 : 1;
      const tw = textWidth(b.text, scale);
      const bh = 8 * scale + 3;
      g.globalAlpha = a * 0.55;
      ui.rect(((W - tw) >> 1) - 4, y - 2, tw + 8, bh, '#000000');
      g.globalAlpha = a;
      ui.text(b.text, W >> 1, y, b.color, { align: 'center', scale });
      g.globalAlpha = 1;
      y += bh + 1;
    }
  }

  private drawChat(H: number): void {
    const st = this.st;
    const now = performance.now();
    const x = 58;
    let y = H - BAR_H - (this.chatOpen ? 26 : 12);
    const maxW = 240;
    const lines = st.chat.slice(-8);
    const rows: { s: string; c: string; age: number }[] = [];
    for (const l of lines) {
      const full = l.from ? `${l.from}: ${l.text}` : l.text;
      for (const part of wrapText(full, maxW)) rows.push({ s: part, c: l.color || C.text, age: (now - l.at) / 1000 });
    }
    const g = ui.ctx;
    for (let i = rows.length - 1; i >= 0 && i >= rows.length - 8; i--) {
      const r = rows[i];
      const alpha = this.chatOpen ? 1 : r.age > 10 ? Math.max(0, 1 - (r.age - 10) / 2) : 1;
      if (alpha <= 0) continue;
      g.globalAlpha = alpha;
      ui.text(r.s, x, y, r.c);
      g.globalAlpha = 1;
      y -= 9;
    }
    if (this.chatOpen) {
      const by = H - BAR_H - 16;
      ui.rect(x - 2, by, maxW + 4, 13, C.goldD);
      ui.rect(x - 1, by + 1, maxW + 2, 11, '#000000');
      const cursor = Math.floor(now / 400) % 2 === 0 ? '_' : '';
      const shown = this.chatText.length > 38 ? this.chatText.slice(-38) : this.chatText;
      ui.text(`${shown}${cursor}`, x + 1, by + 3, C.white, { shadow: null });
    }
  }

  /** Called every frame from the controls while the chat box is open. */
  updateChat(): void {
    for (const ch of input.typed) if (this.chatText.length < 120) this.chatText += ch;
    if (input.pressed.has('Backspace')) this.chatText = this.chatText.slice(0, -1);
    if (input.pressed.has('Enter') || input.pressed.has('NumpadEnter')) {
      const t = this.chatText.trim();
      if (t) this.st.send({ t: 'chat', m: t });
      this.chatText = '';
      this.chatOpen = false;
    }
    if (input.pressed.has('Escape')) {
      this.chatText = '';
      this.chatOpen = false;
    }
  }

  private drawWorldTooltip(): void {
    const st = this.st;
    if (ui.captured) return;
    const o = this.world.hoverObject;
    if (o) {
      const label = objLabel(o, (to) => (to ? parseZone(to).name : ''));
      if (label) ui.textBox([{ s: label, c: C.goldL }]);
      return;
    }
    void st;
  }

  // ─── Items ──────────────────────────────────────────────────────────────

  itemLines(it: Item): { s: string; c?: string }[] {
    const hero = this.st.hero!;
    const lines: { s: string; c?: string }[] = [];
    lines.push({ s: it.name, c: RARITY_COLORS[it.rarity] });
    lines.push({ s: `${RARITY_NAMES[it.rarity]} ${it.slot === 'ring' ? 'Ring' : SLOT_LABELS[it.slot as SlotId]}`, c: C.dim });
    if (it.dmg) lines.push({ s: `Damage: ${it.dmg[0]}-${it.dmg[1]}`, c: C.white });
    if (it.armor) lines.push({ s: `Armor: ${it.armor}`, c: C.white });
    for (const k of Object.keys(it.mods) as StatKey[]) {
      const v = it.mods[k] as number;
      if (!v) continue;
      lines.push({ s: STAT_LABELS[k](v), c: '#6888fc' });
    }
    if (it.flavor) for (const l of wrapText(it.flavor, 170)) lines.push({ s: l, c: '#c88038' });
    if (!canUseItem(it, hero.cls)) lines.push({ s: 'Your class cannot use this', c: C.orange });
    if (it.req > hero.level) lines.push({ s: `Requires level ${it.req}`, c: C.orange });
    else if (it.req > 1) lines.push({ s: `Requires level ${it.req}`, c: C.dim });
    lines.push({ s: `Sells for ${it.value} gold`, c: C.dim });
    return lines;
  }

  private itemTip(it: Item, hint?: string): void {
    const lines = this.itemLines(it);
    if (hint) lines.push({ s: hint, c: C.green });
    const hero = this.st.hero!;
    // Compare with what's worn, side by side.
    const slot: SlotId | null = it.slot === 'ring' ? 'ring1' : (it.slot as SlotId);
    const worn = slot ? hero.equip[slot] : undefined;
    ui.textBox(lines);
    if (worn && worn.uid !== it.uid) {
      const wl = this.itemLines(worn);
      wl.unshift({ s: 'Equipped:', c: C.green });
      let w = 0;
      for (const l of wl) w = Math.max(w, textWidth(l.s));
      const bw = w + 10;
      const bh = wl.length * 9 + 8;
      let w2 = 0;
      for (const l of lines) w2 = Math.max(w2, textWidth(l.s));
      const tipW = w2 + 10;
      let x = input.mx + 10;
      if (x + tipW > ui.W - 2) x = input.mx - tipW - 6;
      const nx = x - bw - 2 >= 2 ? x - bw - 2 : x + tipW + 2;
      ui.boxAt(nx, Math.max(2, Math.min(ui.H - bh - 2, input.my + 4)), wl, bw, bh);
    }
  }

  private itemCell(x: number, y: number, size: number, it: Item | null | undefined, placeholder?: string): boolean {
    const hot = ui.hit(x, y, size, size);
    ui.rect(x, y, size, size, '#000000');
    const unusable = it && (!canUseItem(it, this.st.hero!.cls) || it.req > this.st.hero!.level);
    ui.rect(x + 1, y + 1, size - 2, size - 2, unusable ? '#401010' : hot ? C.hot : '#141014');
    if (it) {
      ui.ctx.drawImage(itemIcon(it.icon, it.rarity), x + ((size - 12) >> 1), y + ((size - 12) >> 1));
      if (it.rarity) {
        ui.rect(x + 1, y + 1, size - 2, 1, RARITY_COLORS[it.rarity]);
      }
    } else if (placeholder) {
      ui.text(placeholder, x + (size >> 1), y + ((size - 7) >> 1), '#403438', { align: 'center', shadow: null });
    }
    if (hot) ui.captured = true;
    return hot;
  }

  // ─── Panels ─────────────────────────────────────────────────────────────

  private panelH(): number {
    return ui.H - BAR_H - 4;
  }

  private panelInventory(W: number): void {
    const st = this.st;
    const hero = st.hero!;
    const pw = 186;
    const x = W - pw - 2;
    const y = 2;
    const h = this.panelH();
    ui.panel(x, y, pw, h, 'Inventory');
    if (ui.button(x + pw - 15, y + 3, 11, 10, 'x')) this.right = null;

    // Paper doll.
    const s = 20;
    const cx = x + (pw >> 1) - (s >> 1);
    const dollY = y + 15;
    const layout: Record<SlotId, [number, number]> = {
      helm: [cx, dollY],
      amulet: [cx + s + 4, dollY],
      weapon: [cx - s - 14, dollY + s + 2],
      armor: [cx, dollY + s + 2],
      offhand: [cx + s + 14, dollY + s + 2],
      gloves: [cx - s - 14, dollY + 2 * (s + 2)],
      boots: [cx + s + 14, dollY + 2 * (s + 2)],
      ring1: [cx - s + 8, dollY + 2 * (s + 2)],
      ring2: [cx + s - 8, dollY + 2 * (s + 2)],
    };
    for (const slot of SLOT_IDS) {
      const [sx, sy] = layout[slot];
      const it = hero.equip[slot];
      if (this.itemCell(sx, sy, s, it, SLOT_LABELS[slot][0])) {
        if (it) {
          ui.tooltip(() => ui.textBox([...this.itemLines(it), { s: 'Click to unequip', c: C.green }]));
          if (input.lmbPressed) st.send({ t: 'unequip', slot });
        } else {
          ui.tooltip(() => ui.textBox([{ s: SLOT_LABELS[slot], c: C.dim }]));
        }
      }
    }

    // Bag grid.
    const cols = 8;
    const cell = 20;
    const bx = x + ((pw - cols * cell) >> 1);
    const by = dollY + 3 * (s + 2) + 4;
    const selling = this.left === 'merchant';
    const stashing = this.left === 'stash';
    for (let i = 0; i < hero.bag.length; i++) {
      const it = hero.bag[i];
      const cxx = bx + (i % cols) * cell;
      const cyy = by + Math.floor(i / cols) * cell;
      if (this.itemCell(cxx, cyy, cell, it)) {
        if (it) {
          const shift = input.down.has('ShiftLeft') || input.down.has('ShiftRight');
          const hint = shift ? 'Click to drop' : selling ? 'Click to sell' : stashing ? 'Click to stash' : 'Click to equip, Shift+click to drop';
          ui.tooltip(() => this.itemTip(it, hint));
          if (input.lmbPressed) {
            if (shift) st.send({ t: 'drop', uid: it.uid });
            else if (selling) st.send({ t: 'sell', uid: it.uid });
            else if (stashing) st.send({ t: 'stash', uid: it.uid, to: 'stash' });
            else st.send({ t: 'equip', uid: it.uid });
          }
        }
      }
    }
    const gy = by + Math.ceil(hero.bag.length / cols) * cell + 2;
    ui.ctx.drawImage(itemIcon('gold', 4), bx, gy - 1);
    ui.text(`${st.me[6]}`, bx + 14, gy, C.goldL);
  }

  private panelChar(): void {
    const st = this.st;
    const hero = st.hero!;
    const d = st.derived!;
    const pw = 186;
    const x = 2;
    const y = 2;
    ui.panel(x, y, pw, this.panelH(), 'Character');
    if (ui.button(x + pw - 15, y + 3, 11, 10, 'x')) this.left = null;
    const cls = CLASSES[hero.cls];
    blitHero(ui.ctx, heroFrame(dressedLook(hero.cls, gearLook(hero.equip)), 0, 0, Math.floor(performance.now() / 333) % 2), x + 8, y + 14);
    ui.text(hero.name, x + 28, y + 14, C.goldL);
    ui.text(`Level ${hero.level} ${cls.name}`, x + 28, y + 23);
    ui.text(`XP ${Math.floor(st.me[5])}/${hero.level >= 50 ? '-' : xpToNext(hero.level)}`, x + 28, y + 32, C.dim);

    let ly = y + 43;
    if (hero.statPts > 0) {
      ui.text(`${hero.statPts} point${hero.statPts > 1 ? 's' : ''} to spend`, x + 110, ly + 10, C.goldL);
    }
    for (const s of STAT_INFO) {
      const base = hero.stats[s.key];
      const total = d[s.key];
      ui.text(s.label, x + 10, ly, C.text);
      ui.text(`${total}`, x + 90, ly, total > base ? C.cyan : C.white, { align: 'right' });
      if (hero.statPts > 0) {
        if (ui.button(x + 96, ly - 2, 11, 10, '+', { col: C.goldL })) st.send({ t: 'stat', k: s.key });
      }
      if (ui.hit(x + 8, ly - 1, 84, 9)) ui.tooltip(() => ui.textBox([{ s: s.label, c: C.goldL }, { s: s.desc }]));
      ly += 11;
    }
    ly += 1;
    ui.rect(x + 8, ly, pw - 16, 1, C.goldD);
    ly += 3;
    const row = (a: string, b: string, col: string = C.white) => {
      ui.text(a, x + 10, ly, C.dim);
      ui.text(b, x + pw - 10, ly, col, { align: 'right' });
      ly += 9;
    };
    const ele = d.fireDmg + d.coldDmg + d.lightDmg + d.poisonDmg;
    row('Damage', `${d.minDmg}-${d.maxDmg}${ele ? ` +${ele}` : ''}${d.dmgPct ? ` +${d.dmgPct}%` : ''}`);
    row('Attack speed', `+${Math.round(d.atkSpd)}%`);
    row('Crit', `${Math.round(d.crit)}% x${(1 + d.critDmg / 100).toFixed(2)}`);
    row('Armor', `${d.armor}`);
    row('Life', `${st.me[2]}  (+${d.hpRegen.toFixed(1)}/s)`);
    row('Mana', `${st.me[3]}  (+${d.mpRegen.toFixed(1)}/s)`);
    row('Move speed', `${d.moveSpeed.toFixed(1)}`);
    if (d.lifeSteal || d.manaSteal) row('Steal L/M', `${d.lifeSteal}% / ${d.manaSteal}%`);
    if (d.magicFind || d.goldFind) row('Magic/Gold', `+${d.magicFind}% / +${d.goldFind}%`);
    ly += 2;
    ui.text('Resists', x + 10, ly, C.dim);
    ui.text(`${d.resFire}`, x + 74, ly, '#f87858', { align: 'right' });
    ui.text(`${d.resCold}`, x + 104, ly, '#3cbcfc', { align: 'right' });
    ui.text(`${d.resLight}`, x + 134, ly, '#f8d878', { align: 'right' });
    ui.text(`${d.resPoison}`, x + 164, ly, '#58d854', { align: 'right' });
    ly += 10;
    // Slides aside if it would run under the life orb on short screens.
    const tallyX = ly + 8 > ui.H - 56 ? x + 58 : x + 10;
    ui.text(`Slain ${hero.kills}  Deaths ${hero.deaths}`, tallyX, ly, C.dim);
  }

  private panelSkills(): void {
    const st = this.st;
    const hero = st.hero!;
    const pw = 186;
    const x = 2;
    const y = 2;
    const h = this.panelH();
    ui.panel(x, y, pw, h, 'Skills');
    if (ui.button(x + pw - 15, y + 3, 11, 10, 'x')) this.left = null;
    ui.text(hero.skillPts ? `${hero.skillPts} point${hero.skillPts > 1 ? 's' : ''} to spend` : 'No points to spend', x + 8, y + 14, hero.skillPts ? C.goldL : C.dim);
    const cls = CLASSES[hero.cls];
    // Rows stop above the life orb, which sticks up over the corner of this panel.
    const rowH = Math.max(19, Math.min(26, Math.floor((ui.H - 56 - (y + 24)) / cls.skills.length)));
    let ry = y + 24;
    for (const k of cls.skills) {
      const def = SKILLS[k];
      const rank = hero.skills[k] ?? 0;
      const locked = def.tier > hero.level;
      const rowHot = ui.hit(x + 6, ry, pw - 12, rowH - 1);
      ui.rect(x + 6, ry, pw - 12, rowH - 1, rowHot ? C.hot : '#141014');
      ui.rect(x + 7, ry + 1, 18, 18, '#000000');
      ui.ctx.globalAlpha = locked ? 0.3 : rank ? 1 : 0.55;
      ui.ctx.drawImage(skillIcon(def.icon), x + 8, ry + 2);
      ui.ctx.globalAlpha = 1;
      ui.text(def.name, x + 28, ry + 1, locked ? '#605858' : C.goldL, { shadow: null });
      if (locked) {
        ui.text(`Level ${def.tier}`, x + 28, ry + 10, '#7c6c60', { shadow: null });
      } else if (rank) {
        ui.text(`${rank}/${def.maxRank}`, x + 28, ry + 10, C.text, { shadow: null });
        // Binding chips.
        for (let b = 0; b < 6; b++) {
          const on = hero.binds[b] === k;
          const bx = x + 58 + b * 12;
          if (ui.button(bx, ry + 9, 12, 10, BIND_LABELS[b], { active: on })) {
            st.send({ t: 'bind', i: b, k: on ? null : k });
          }
        }
      } else {
        ui.text('Unlearned', x + 28, ry + 10, C.dim, { shadow: null });
      }
      if (!locked && rank < def.maxRank && hero.skillPts > 0) {
        if (ui.button(x + pw - 21, ry + 2, 13, 12, '+', { col: C.goldL })) st.send({ t: 'skill', k });
      }
      if (rowHot) ui.tooltip(() => this.skillTip(k));
      ry += rowH;
    }
  }

  private panelMerchant(): void {
    const st = this.st;
    const hero = st.hero!;
    const pw = 186;
    const x = 2;
    const y = 2;
    ui.panel(x, y, pw, 118, 'Quill the Merchant');
    if (ui.button(x + pw - 15, y + 3, 11, 10, 'x')) this.left = null;
    ui.text('"Potions, friend? Coin first."', x + 10, y + 15, C.dim);
    const hpP = POTION_PRICE.hp(hero.level);
    const mpP = POTION_PRICE.mp(hero.level);
    ui.ctx.drawImage(itemIcon('potHp', 0), x + 10, y + 28);
    ui.text(`Life Potion ${hpP}g  (${st.me[7]}/${MAX_POTIONS})`, x + 24, y + 30);
    if (ui.button(x + 24, y + 40, 40, 13, 'Buy', { disabled: st.me[6] < hpP || st.me[7] >= MAX_POTIONS })) st.send({ t: 'buy', k: 'hp', n: 1 });
    if (ui.button(x + 68, y + 40, 40, 13, 'Buy 5', { disabled: st.me[6] < hpP || st.me[7] >= MAX_POTIONS })) st.send({ t: 'buy', k: 'hp', n: 5 });
    ui.ctx.drawImage(itemIcon('potMp', 0), x + 10, y + 58);
    ui.text(`Mana Potion ${mpP}g  (${st.me[8]}/${MAX_POTIONS})`, x + 24, y + 60);
    if (ui.button(x + 24, y + 70, 40, 13, 'Buy', { disabled: st.me[6] < mpP || st.me[8] >= MAX_POTIONS })) st.send({ t: 'buy', k: 'mp', n: 1 });
    if (ui.button(x + 68, y + 70, 40, 13, 'Buy 5', { disabled: st.me[6] < mpP || st.me[8] >= MAX_POTIONS })) st.send({ t: 'buy', k: 'mp', n: 5 });
    ui.text('Click items in your pack to sell.', x + 10, y + 90, C.dim);
    ui.ctx.drawImage(itemIcon('gold', 4), x + 10, y + 99);
    ui.text(`${st.me[6]} gold`, x + 24, y + 101, C.goldL);
  }

  private panelTrainer(): void {
    const st = this.st;
    const hero = st.hero!;
    const pw = 186;
    const x = 2;
    const y = 2;
    ui.panel(x, y, pw, 90, 'Oda the Trainer');
    if (ui.button(x + pw - 15, y + 3, 11, 10, 'x')) this.left = null;
    const lines = wrapText('"Regret your path? For a fee, your mind forgets every point you spent."', pw - 20);
    lines.forEach((l, i) => ui.text(l, x + 10, y + 15 + i * 9, C.dim));
    const cost = RESPEC_COST(hero.level);
    if (ui.button(x + 26, y + 58, pw - 52, 15, `Reset points: ${cost}g`, { disabled: st.me[6] < cost })) st.send({ t: 'respec' });
  }

  private panelStash(): void {
    const st = this.st;
    const hero = st.hero!;
    const pw = 186;
    const x = 2;
    const y = 2;
    ui.panel(x, y, pw, 130, 'Stash');
    if (ui.button(x + pw - 15, y + 3, 11, 10, 'x')) this.left = null;
    const cols = 8;
    const cell = 20;
    const bx = x + ((pw - cols * cell) >> 1);
    const by = y + 15;
    for (let i = 0; i < hero.stash.length; i++) {
      const it = hero.stash[i];
      const cx = bx + (i % cols) * cell;
      const cy = by + Math.floor(i / cols) * cell;
      if (this.itemCell(cx, cy, cell, it) && it) {
        ui.tooltip(() => ui.textBox([...this.itemLines(it), { s: 'Click to take', c: C.green }]));
        if (input.lmbPressed) st.send({ t: 'stash', uid: it.uid, to: 'bag' });
      }
    }
    ui.text('Shared by nobody. Yours forever.', x + 10, by + 4 * cell + 3, C.dim);
  }

  private panelGate(W: number, H: number): void {
    const st = this.st;
    const hero = st.hero!;
    const pw = 240;
    const allies = st.party.filter((p) => p.pid !== st.myPid && p.zone !== HUB_ZONE && !parseZone(p.zone).pvp);
    const h = Math.min(H - BAR_H - 8, 32 + REALMS.length * 15 + (allies.length ? allies.length * 15 + 12 : 0) + 20);
    const x = (W - pw) >> 1;
    const y = Math.max(2, ((H - BAR_H - h) >> 1));
    ui.panel(x, y, pw, h, 'Waygate');
    if (ui.button(x + pw - 15, y + 3, 11, 10, 'x')) this.center = null;
    let ry = y + 15;
    if (ui.button(x + 8, ry, pw - 16, 13, 'Return to where you left off')) {
      st.send({ t: 'warp', z: 'return' });
      this.center = null;
    }
    ry += 15;
    for (const r of REALMS) {
      const open = r.idx <= hero.progress;
      const label = open ? `${r.name}  (lvl ${r.level})` : '???';
      if (ui.button(x + 8, ry, pw - 16, 13, label, { disabled: !open })) {
        st.send({ t: 'warp', z: zoneId(r.idx, 0) });
        this.center = null;
      }
      if (open && ui.hit(x + 8, ry, pw - 16, 13)) {
        ui.tooltip(() => {
          const lines = wrapText(r.blurb, 180).map((s) => ({ s, c: C.dim }));
          ui.textBox([{ s: r.name, c: C.goldL }, ...lines, { s: `Lair of ${r.bossName}`, c: C.orange }]);
        });
      }
      ry += 15;
    }
    if (allies.length) {
      ui.text('Warp to an ally', x + 10, ry + 2, C.goldL);
      ry += 12;
      for (const a of allies) {
        if (ui.button(x + 8, ry, pw - 16, 13, `${a.name} - ${parseZone(a.zone).name}`, { col: '#58f898' })) {
          st.send({ t: 'warp', ally: a.pid });
          this.center = null;
        }
        ry += 15;
      }
    }
  }

  private panelArena(W: number, H: number): void {
    const st = this.st;
    const pw = 220;
    const h = 92;
    const x = (W - pw) >> 1;
    const y = (H - BAR_H - h) >> 1;
    ui.panel(x, y, pw, h, 'The Blood Arena');
    if (ui.button(x + pw - 15, y + 3, 11, 10, 'x')) this.center = null;
    ui.text('Hero against hero. No loot, all glory.', x + 12, y + 16, C.dim);
    const pick = (team: 'ffa' | 'red' | 'blue') => {
      st.send({ t: 'arena', team });
      this.center = null;
    };
    if (ui.button(x + 12, y + 30, pw - 24, 15, 'Free for all')) pick('ffa');
    if (ui.button(x + 12, y + 48, (pw - 28) >> 1, 15, 'Red team', { col: '#f83800' })) pick('red');
    if (ui.button(x + 14 + ((pw - 28) >> 1), y + 48, (pw - 28) >> 1, 15, 'Blue team', { col: '#3cbcfc' })) pick('blue');
    ui.text('Leave through the portal, or press T.', x + 12, y + 70, C.dim);
  }

  private panelHelp(W: number, H: number): void {
    const pw = 300;
    const rows: [string, string][] = [
      ['Left click', 'Move, attack, pick up, talk'],
      ['Shift+Left', 'Attack on the spot'],
      ['Right click', 'Use right-click skill at cursor'],
      ['1 2 3 4', 'Use bound skills at cursor'],
      ['WASD / arrows', 'Move'],
      ['Q / E', 'Life / mana potion'],
      ['C / I / K', 'Character / Inventory / Skills'],
      ['T', 'Warp to the Nexus, with a portal back'],
      ['Tab', 'Full map'],
      ['Alt', 'Show item names'],
      ['Enter', 'Chat'],
      ['M', 'Mute   (N for music)'],
      ['P', 'Voice: join, opt out, mute players'],
      ['V', 'Hold to talk'],
    ];
    const h = rows.length * 10 + 40;
    const x = (W - pw) >> 1;
    const y = Math.max(2, (H - BAR_H - h) >> 1);
    ui.panel(x, y, pw, h, 'How to Play');
    if (ui.button(x + pw - 15, y + 3, 11, 10, 'x')) this.center = null;
    rows.forEach(([a, b], i) => {
      ui.text(a, x + 12, y + 16 + i * 10, C.goldL);
      ui.text(b, x + 104, y + 16 + i * 10, C.text);
    });
    ui.text(`Invite friends with room code ${this.st.roomCode}.`, x + 12, y + h - 18, C.green);
  }

  private panelMenu(W: number, H: number): void {
    const pw = 150;
    const h = 104;
    const x = (W - pw) >> 1;
    const y = (H - BAR_H - h) >> 1;
    ui.panel(x, y, pw, h, 'Menu');
    if (ui.button(x + 12, y + 16, pw - 24, 14, 'Resume')) this.center = null;
    if (ui.button(x + 12, y + 32, pw - 24, 14, `Sound: ${audio.muted ? 'off' : 'on'}`)) audio.toggleMute();
    if (ui.button(x + 12, y + 48, pw - 24, 14, `Music: ${audio.musicOn ? 'on' : 'off'}`)) audio.toggleMusic();
    if (ui.button(x + 12, y + 64, pw - 24, 14, `Voice: ${this.voice.mode}`)) this.center = 'voice';
    if (ui.button(x + 12, y + 80, pw - 24, 14, 'Leave room', { col: C.orange })) this.onLeave?.();
  }

  private panelVoice(W: number, H: number): void {
    const v = this.voice;
    const st = this.st;
    const others = st.party.filter((p) => p.pid !== st.myPid);
    const pw = 260;
    const rowH = 15;
    const listY = 58;
    const h = listY + Math.max(1, others.length) * rowH + 8;
    const x = (W - pw) >> 1;
    // Placed as if the room were full. Centring on who's here would move the buttons as people come and go.
    const y = Math.max(2, (H - BAR_H - (listY + (MAX_PLAYERS - 1) * rowH + 8)) >> 1);
    ui.panel(x, y, pw, h, 'Voice');
    if (ui.button(x + pw - 15, y + 3, 11, 10, 'x')) this.center = null;

    const bw = Math.floor((pw - 24 - 4) / 3);
    VOICE_MODES.forEach(([mode, label], i) => {
      if (ui.button(x + 12 + i * (bw + 2), y + 15, bw, 14, label, { active: v.mode === mode })) v.setMode(mode);
    });
    const about = VOICE_MODES.find(([mode]) => mode === v.mode)![2];
    ui.text(about, x + 12, y + 33, C.text);
    const note = this.voiceNote();
    if (note) ui.text(note.text, x + 12, y + 43, note.col);

    if (!others.length) {
      ui.text('Nobody else is in this room.', x + 12, y + listY, C.dim);
      return;
    }
    others.forEach((p, i) => {
      const ry = y + listY + i * rowH;
      const entry = v.entry(p.pid);
      const muted = !!entry && v.isMuted(entry.vid);
      ui.text(p.name, x + 12, ry + 3, muted ? C.dim : C.text);
      let state = 'Not in voice';
      let col: string = C.dim;
      if (muted) {
        state = 'Muted';
        col = C.orange;
      } else if (entry && v.isSpeaking(p.pid)) {
        state = 'Talking';
        col = C.green;
      } else if (entry) {
        state = entry.gen !== undefined ? 'Mic on' : 'Listening';
        col = C.text;
      }
      ui.text(state, x + 136, ry + 3, col);
      if (entry && ui.button(x + pw - 58, ry, 46, 13, muted ? 'Unmute' : 'Mute')) v.toggleMute(entry.vid);
    });
  }

  /** Anything about voice that the player's choice of mode doesn't already explain. */
  private voiceNote(): { text: string; col: string } | null {
    const v = this.voice;
    if (v.available === false) return { text: 'Voice is not set up on this server.', col: C.orange };
    if (v.available === null) return { text: 'Voice needs this room to update first.', col: C.goldL };
    if (v.status === 'failed') return { text: 'Could not connect. Choose a mode to retry.', col: C.orange };
    if (v.status === 'reconnecting') return { text: 'Reconnecting...', col: C.orange };
    if (v.awaitingMic) return { text: 'Allow your microphone in the browser.', col: C.goldL };
    if (v.mode !== 'off' && v.status !== 'connected') return { text: 'Connecting...', col: C.goldL };
    return null;
  }

  private drawDeath(W: number, H: number): void {
    const st = this.st;
    if (!(st.me[9] & ME_DOWNED)) return;
    const g = ui.ctx;
    g.fillStyle = 'rgba(80,0,0,0.25)';
    g.fillRect(0, 0, W, H);
    ui.text('YOU HAVE FALLEN', W >> 1, (H >> 1) - 50, '#f83800', { align: 'center', scale: 2 });
    const others = st.party.filter((p) => p.pid !== st.myPid && p.zone === st.zone?.id && !p.dead);
    ui.text(others.length ? 'An ally can revive you, or you may return to the Nexus.' : 'You may return to the Nexus. It will cost some gold.', W >> 1, (H >> 1) - 26, C.text, { align: 'center' });
    if (ui.button((W >> 1) - 60, (H >> 1) - 12, 120, 16, 'Return to Nexus')) st.send({ t: 'respawn' });
  }

  private drawConnection(W: number, H: number): void {
    const st = this.st;
    if (st.connected) return;
    const g = ui.ctx;
    g.fillStyle = 'rgba(0,0,0,0.6)';
    g.fillRect(0, 0, W, H);
    ui.text(st.statusText || 'Reconnecting...', W >> 1, H >> 1, C.goldL, { align: 'center' });
  }
}

export type { Mods };
